import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { AlertContext } from '../contexts/alert-context';
import type { TriggerPricingUpdateRequest, VendorPricingStats } from '../data/admin';
import { useAdminService } from './admin-service';

export function usePricingStats() {
  const { addErrorAlert } = useContext(AlertContext);
  const adminService = useAdminService();

  return useQuery<Record<string, VendorPricingStats>>({
    queryKey: ['admin', 'pricing', 'stats'],
    queryFn: async () => {
      return adminService.getPricingStats().catch((error: Error) => {
        addErrorAlert(`Failed to fetch pricing stats: ${error.message}`);
        throw error;
      });
    },
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
    gcTime: 10 * 60 * 1000, // Garbage collect data after 10 minutes
    retry: 2,
  });
}

export function useTriggerPricing() {
  const { addErrorAlert, addSuccessAlert } = useContext(AlertContext);
  const queryClient = useQueryClient();
  const adminService = useAdminService();

  return useMutation({
    mutationFn: (params: TriggerPricingUpdateRequest) => {
      return adminService.triggerPricingUpdate(params);
    },
    onSuccess: (_, variables) => {
      addSuccessAlert(`Pricing update triggered for ${variables.vendor_name}`);
      // Delay invalidation by 15 seconds to allow the pricing job to complete
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['admin', 'pricing', 'stats'] });
      }, 15000);
    },
    onError: (error: Error, variables) => {
      addErrorAlert(
        `Failed to trigger pricing update for ${variables.vendor_name}: ${error.message}`
      );
    },
  });
}

export function useTriggerAllNysla() {
  const { addErrorAlert, addSuccessAlert } = useContext(AlertContext);
  const adminService = useAdminService();

  return useMutation({
    mutationFn: () => adminService.triggerAllNyslaPricing(),
    onSuccess: () => {
      addSuccessAlert(
        'NYSLA pricing update triggered for all NY vendors (may take several minutes)'
      );
    },
    onError: (error: Error) => {
      addErrorAlert(`Failed to trigger NYSLA pricing update: ${error.message}`);
    },
  });
}
